/**
 * B&Y APPLICATION FRONTEND
 * (c) 2025
 */

/**
 * VIDSTACK STYLES
 */
import 'vidstack/player/styles/base.css'
import 'vidstack/player/styles/plyr/theme.css'

/**
 * POLYFILLS
 */
import ResizeObserver from 'resize-observer-polyfill'
window.ResizeObserver = window.ResizeObserver || ResizeObserver

/**
 * JUPITER IMPORTS
 */
import {
  Application,
  // Cookies,
  Events,
  StickyHeader,
  MobileMenu,
  Lazyload,
  // Links,
  Toggler,
  Typography,
  Dom,
  Dataloader,
} from '@brandocms/jupiter'

/**
 * APP SPECIFIC MODULE IMPORTS
 */
import ViewTransitions from './modules/ViewTransitions'
import TriangleNav from './modules/TriangleNav'
import ListNav from './modules/ListNav'
import NavTeleport from './modules/NavTeleport'
import HashToggleHandler from './modules/HashToggleHandler'
import TabHandler from './modules/TabHandler'
import AsideNavigation from './modules/AsideNavigation'
import FullWidthExtractor from './modules/FullWidthExtractor'
import Annotations from './modules/Annotations'
import VideoBlock from './modules/VideoBlock'
import CourseSidebar from './modules/CourseSidebar'

/**
 * CONFIG IMPORTS
 */
import configureBreakpoints from './config/BREAKPOINTS'
import configureHeader from './config/HEADER'
import configureMobileMenu from './config/MOBILE_MENU'
import '../css/app.css'
import { initAccordionAccessibility } from './utils/accordionAccessibility'

console.log('—— BBiS v1.0.0-alpha.0')

const app = new Application({
  respectReducedMotion: false,
  breakpointConfig: configureBreakpoints,
  faderOpts: {
    fadeIn: callback => {
      document.body.classList.remove('unloaded')
      callback()
    },
  },
})

// Reusable function to initialize footer video (show video when playing, fallback to SVG)
app.initFooterVideo = () => {
  const footerVideo = Dom.find('[data-footer-video]')
  if (footerVideo) {
    const illustration = footerVideo.closest('.illustration')
    footerVideo.addEventListener('playing', () => {
      illustration?.classList.add('video-playing')
    })
  }
}

// Reusable function to initialize accordion expand/collapse buttons
app.initAccordionExpandButtons = () => {
  const expandAllButtons = Dom.all('[data-expand-all]')
  expandAllButtons.forEach(button => {
    // Skip if already initialized
    if (button.hasAttribute('data-expand-initialized')) return
    button.setAttribute('data-expand-initialized', '')

    button.addEventListener('click', () => {
      const accordion = button.closest('[b-tpl="accordion"]')
      if (!accordion) return

      const toggleButtons = Dom.all('[data-toggle-trigger]', accordion)
      const toggleContainers = Dom.all('[data-toggle]', accordion)

      // Check if all are expanded
      const allExpanded = Array.from(toggleContainers).every(container =>
        container.classList.contains('open')
      )

      if (allExpanded) {
        // Collapse all
        toggleButtons.forEach(btn => {
          const container = btn.closest('[data-toggle]')
          if (container && container.classList.contains('open')) {
            btn.click()
          }
        })
        button.removeAttribute('data-all-expanded')
        button.querySelector('.button-text').textContent = 'Åpne alle'
      } else {
        // Expand all
        toggleButtons.forEach(btn => {
          const container = btn.closest('[data-toggle]')
          if (container && !container.classList.contains('open')) {
            btn.click()
          }
        })
        button.setAttribute('data-all-expanded', '')
        button.querySelector('.button-text').textContent = 'Lukk alle'
      }
    })
  })
}

app.registerCallback(Events.APPLICATION_READY, () => {
  // app.links = new Links(app, {
  //   triggerEvents: false,
  //   mobileMenuDelay: 150,
  //   scrollOffsetNav: true,
  // })

  // Extract full-width blocks early before other modules initialize
  app.fullWidthExtractor = new FullWidthExtractor(app)

  app.lazyload = new Lazyload(app, { useNativeLazyloadIfAvailable: false })
  app.viewTransitions = new ViewTransitions(app)
  app.triangleNav = new TriangleNav(app)
  app.listNav = new ListNav(app)
  app.navTeleport = new NavTeleport(app)
  app.hashToggleHandler = new HashToggleHandler(app)

  // Initialize tab handlers for each tab container
  app.tabHandlers = []
  const tabContainers = Dom.all('[b-tpl="tabContainer"]')
  tabContainers.forEach(container => {
    app.tabHandlers.push(new TabHandler(app, container))
  })

  // Initialize aside navigation for each container
  app.asideNavigations = []
  const asideNavContainers = Dom.all('[data-create-navigation]')
  asideNavContainers.forEach(container => {
    app.asideNavigations.push(new AsideNavigation(app, container))
  })

  // Initialize annotations
  app.annotations = new Annotations(app)

  // Initialize video blocks
  app.videoBlocks = []
  const videoBlocks = Dom.all('[data-video-block]')
  videoBlocks.forEach(block => {
    app.videoBlocks.push(new VideoBlock(app, block))
  })

  // Initialize expand/collapse all buttons for accordions
  app.initAccordionExpandButtons()

  // Initialize course sidebars
  app.courseSidebars = []
  const courseSidebars = Dom.all('[data-course-sidebar]')
  courseSidebars.forEach(sidebar => {
    app.courseSidebars.push(new CourseSidebar(app, sidebar))
  })

  // Initialize expandable text triggers (outside of sidebars too)
  const expandTriggers = Dom.all('[data-expandable-trigger]')
  expandTriggers.forEach(trigger => {
    if (trigger.hasAttribute('data-expandable-initialized')) return
    trigger.setAttribute('data-expandable-initialized', '')

    trigger.addEventListener('click', () => {
      const container = trigger.closest('[b-tpl="expandableText"]')
      if (!container) return

      const content = container.querySelector('[data-expandable-content]')
      if (!content) return

      const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

      if (isExpanded) {
        content.hidden = true
        trigger.setAttribute('aria-expanded', 'false')
      } else {
        content.hidden = false
        trigger.setAttribute('aria-expanded', 'true')
      }
    })
  })

  // Initialize module hero toggle
  const moduleHeroes = Dom.all('[data-module-hero]')
  moduleHeroes.forEach(hero => {
    if (hero.hasAttribute('data-hero-initialized')) return
    hero.setAttribute('data-hero-initialized', '')

    const toggle = hero.querySelector('.hero-toggle')
    if (!toggle) return

    toggle.addEventListener('click', () => {
      const isCollapsed = hero.classList.toggle('is-collapsed')
      toggle.setAttribute('aria-expanded', !isCollapsed)
      toggle.setAttribute(
        'aria-label',
        isCollapsed ? 'Utvid modulinformasjon' : 'Minimer modulinformasjon'
      )
    })
  })

  // Initialize footer video
  app.initFooterVideo()
})

app.registerCallback(Events.APPLICATION_PRELUDIUM, () => {
  app.header = new StickyHeader(app, configureHeader(app))
  app.mobileMenu = new MobileMenu(app, configureMobileMenu(app))

  app.typo = new Typography()
  app.togglers = []

  const togglers = Dom.all('[data-toggle]')
  togglers.forEach(toggleEl => {
    app.togglers.push(new Toggler(app, toggleEl))
  })

  // Initialize accordion aria-expanded handling (WCAG 4.1.2)
  initAccordionAccessibility()

  app.dataloaders = []

  const loaderEls = Dom.all('[data-loader]')
  Array.from(loaderEls).forEach(loaderEl => {
    app.dataloaders.push(
      new Dataloader(app, loaderEl, {
        onFetch: dataloader => {
          console.log('Data loaded:', dataloader)
          new Lazyload(app, { useNativeLazyloadIfAvailable: false }, dataloader.$canvasEl)
        },
      })
    )
  })
})

app.registerCallback(Events.APPLICATION_REVEALED, () => {})

// trigger ready state
if (
  document.attachEvent ? document.readyState === 'complete' : document.readyState !== 'loading'
) {
  app.initialize()
} else {
  document.addEventListener('DOMContentLoaded', app.initialize.apply(app))
}
